<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { VueSpinner } from 'vue3-spinners'
import TrustpilotWidget from "@/components/TrustPilot/TrustpilotWidget.vue";
import {useFormatStore} from "@/stores/formatStore";

const { t } = useI18n()
const showOnSpotDetails = ref(false);
const formatStore = useFormatStore();

defineProps<{
  availabilities: any
  priceDetails: any
  yacht: any
  dateDays: number
  loading: boolean
}>()
</script>

<template>
  <Transition :css="false">
    <!-- Loader -->
    <div v-if="loading" class="flex justify-center items-center min-h-[200px]">
      <VueSpinner :size="50" color="#1b84ff"/>
    </div>

    <!-- Available -->
    <div v-else-if="availabilities?.available">
      <div class="responsive-title-6 font-bold mb-3">{{ t('priceDetails') }}</div>
      <div class="flex flex-col gap-y-1 boat-charges">
        <div class="boat-charges responsive-body-small flex flex-col gap-y-1.5">
          <div class="flex items-center gap-2 font-bold">
            <!-- Free cancellation -->
            <el-popover
                placement="top"
                trigger="hover"
                :content="t('freeCancellationTooltip')"
                :width="150"
                :popper-style="{ 'z-index': '10', 'text-align': 'center', 'font-size': '.75rem', 'font-weight': '700'}"
                popper-class="text-black pointer-events-none"
            >
              <template #reference>
                <div class="rounded-3xl px-3 py-1 bg-bw-silver cursor-default">
                  {{ t('freeCancellation') }}
                </div>
              </template>
            </el-popover>
            <!-- Price match -->
            <el-popover
                placement="top"
                trigger="hover"
                :content="t('priceMatchTooltip')"
                :width="150"
                :popper-style="{ 'z-index': '10', 'text-align': 'center', 'font-size': '.75rem', 'font-weight': '700'}"
                popper-class="text-black pointer-events-none"
            >
              <template #reference>
                <div class="rounded-3xl px-3 py-1 bg-bw-white cursor-default">
                  {{ t('priceMatch') }}
                </div>
              </template>
            </el-popover>
          </div>
          <div class="flex justify-between">
            <div class="flex items-center gap-2">
              <!-- Boat Price and Special Offer -->
              <div class="flex gap-x-2 stack-column">
                <div class="text-bw-darkGrey responsive-body-small ">
                  {{ t('boatPrice', { days: dateDays }) }}
                </div>
                <div class="text-primary-blue responsive-body-small" v-if="priceDetails.hasOffer">
                  {{ t('specialOffer') }}
                </div>
              </div>

              <!-- Offer Price outside -->
              <div
                  v-if="availabilities.discount > 0"
                  class="responsive-body-small bg-primary-blue text-white w-fit h-fit py-px px-2 rounded-15xl"
              >
                -{{ Math.round(availabilities.discount) }}%
              </div>
            </div>

            <div class="flex gap-2">
              <span class="responsive-body-large line-through text-bw-darkGrey align-center flex" v-if="priceDetails.hasOffer">
                  {{ formatStore.formatCurrency(priceDetails.basePrice) }}
              </span>
            </div>
          </div>
        </div>
        <div class="flex justify-between" v-for="amenity in priceDetails.amenities.advance"
             :key="amenity.id">
          <span class="text-bw-darkGrey max-w-20vw">{{ amenity.name }}</span>
          <span>{{
              amenity.subtotal > 0 ? formatStore.formatCurrency(amenity.subtotal) : t('noCost')
            }}</span>
        </div>
      </div>

      <div class="flex flex-col my-2">
        <div class="flex align-center justify-between">
          <span class="responsive-body-large font-semibold">{{ t('totalPrice') }}</span>
          <span
              class="final-price responsive-title-4 font-bold">
                   {{ formatStore.formatCurrency(priceDetails.total) }}
                </span>
        </div>
      </div>

      <div class="flex align-center justify-between" v-if="yacht.deposit">
        <span class=" responsive-body-small text-bw-darkGrey">
          {{ t('securityDeposit') }}
        </span>
        <span class="responsive-body-medium">
          {{ formatStore.formatCurrency(yacht.deposit) }}
        </span>
      </div>

      <div class="flex flex-col mb-2 boat-charges " v-if="priceDetails?.totalOnSpot">
        <div class="flex align-center justify-between color-gray">
          <div class="responsive-body-small text-bw-darkGrey">
            {{ t('yacht.price.payableOnSpot') }}
            <span
                @click="showOnSpotDetails = !showOnSpotDetails"
                class="responsive-body-small cursor-pointer text-black transition-opacity hover:opacity-60 text-end ml-2 my-5"
            >
              {{ (showOnSpotDetails ? "-" : "+") + t('yacht.price.showOnSpotDetails') }}
            </span>
            <br/>
            <span class="responsive-body-small text-bw-darkGrey">({{ t('yacht.price.payableOnSpotDetails') }})</span>
          </div>
          <span class=" responsive-body-medium ">
                     {{ formatStore.formatCurrency(priceDetails.totalOnSpot) }}
                    </span>
        </div>
        <div v-if="showOnSpotDetails">
          <div class="flex justify-between py-1 responsive-body-small" v-for="amenity in priceDetails.amenities.separate"
               :key="amenity.id">
            <span class="max-w-30vw responsive-body-small text-bw-darkGrey">{{ amenity.name }}</span>
            <span class="responsive-body-medium">{{
                amenity.subtotal > 0 ? formatStore.formatCurrency(amenity.subtotal) : t('noCost')
              }}</span>
          </div>
        </div>
      </div>
      
      <!-- Button -->
      <slot name="button" />
      
      <!-- The minus margin here is placed cause we cannot change the css Trustpilot iframe -->
      <div class="w-full flex justify-start pt-5 ml-[-15px]">
        <TrustpilotWidget
            theme="light"
        />
      </div>
    </div>

    <!-- No results -->
    <div v-else class="container flex flex-col justify-center items-center py-10 gap-4">
      <img src="@/assets/images/search-results.svg" alt="No Results">
      <div class="font-bold responsive-title-6 text-center">
        {{ t('search.couldntFindDataDescTitle') }}
      </div>
      <div class="text-center">
        {{ t('search.couldntFindDataDesc') }}
      </div>
    </div>
  </Transition>
</template>

<style scoped>
@media (max-width: 1480px) and (min-width: 1111px) {
  .stack-column {
    flex-direction: column;
    gap: 4px;
  }
}

@media (max-width: 580px) {
  .stack-column {
    flex-direction: column;
    gap: 4px;
  }
}
</style>